import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import VistaCalcLogo from './VistaCalcLogo';
import { mortgageAPI } from '../services/api';

interface MortgageFormData {
  property_price_eur: number;
  deposit_eur: number;
  interest_rate_pct: number;
  loan_term_years: number;
  mortgage_type: string;
  fees_eur: number;
  monthly_expenses_eur: number;
}

const MortgageCalculator: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const   [formData, setFormData] = useState<MortgageFormData>({
    property_price_eur: 1000000,
    deposit_eur: 250000,
    interest_rate_pct: 5,
    loan_term_years: 25,
    mortgage_type: 'fixed',
    fees_eur: 10000,
    monthly_expenses_eur: 800,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name.includes('rate') || name.includes('percent') ? parseFloat(value) || 0 : parseFloat(value) || 0
    }));
  };

  const handleCalculate = async () => {
    setLoading(true);
    setError(null);
    setResults(null);

    try {
      console.log('Sending mortgage data:', formData);
      const response = await mortgageAPI.calculateMortgage(formData);
      console.log('Mortgage API response:', response);
      setResults(response);
    } catch (err: any) {
      console.error('Mortgage calculation error:', err);
      setError(err.message || 'An error occurred while calculating mortgage');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  return (
    <div className="min-h-screen bg-vista-silver-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-vista-silver-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="text-vista-silver-600 hover:text-vista-teal-500 transition-colors"
              >
                ← Back to Home
              </button>
              <VistaCalcLogo size="sm" />
            </div>
            <h1 className="text-xl font-bold text-vista-silver-900">Mortgage Calculator</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column - Inputs */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-vista-silver-200">
            <h2 className="text-xl font-bold text-vista-silver-900 mb-6">Mortgage Parameters</h2>
            
            <div className="space-y-6">
              {/* Property Price */}
              <div>
                <label className="block text-sm font-medium text-vista-silver-700 mb-2">
                  Property Price (€)
                </label>
                <input
                  type="number"
                  name="property_price_eur"
                  value={formData.property_price_eur || ''}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-vista-silver-300 rounded-xl focus:ring-2 focus:ring-vista-teal-500 focus:border-vista-teal-500 transition-colors"
                  placeholder="1000000"
                />
              </div>

              {/* Deposit */}
              <div>
                <label className="block text-sm font-medium text-vista-silver-700 mb-2">
                  Deposit Amount (€)
                </label>
                <input
                  type="number"
                  name="deposit_eur"
                  value={formData.deposit_eur || ''}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-vista-silver-300 rounded-xl focus:ring-2 focus:ring-vista-teal-500 focus:border-vista-teal-500 transition-colors"
                  placeholder="250000"
                />
              </div>

              {/* Interest Rate and Loan Term */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-vista-silver-700 mb-2">
                    Interest Rate (%)
                  </label>
                  <input
                    type="number"
                    name="interest_rate_pct"
                    value={formData.interest_rate_pct || ''}
                    onChange={handleInputChange}
                    step="0.1"
                    className="w-full px-4 py-3 border border-vista-silver-300 rounded-xl focus:ring-2 focus:ring-vista-teal-500 focus:border-vista-teal-500 transition-colors"
                    placeholder="5.0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-vista-silver-700 mb-2">
                    Loan Term (Years)
                  </label>
                  <input
                    type="number"
                    name="loan_term_years"
                    value={formData.loan_term_years || ''}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-vista-silver-300 rounded-xl focus:ring-2 focus:ring-vista-teal-500 focus:border-vista-teal-500 transition-colors"
                    placeholder="25"
                  />
                </div>
              </div>

              {/* Mortgage Type and Fees */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-vista-silver-700 mb-2">
                    Mortgage Type
                  </label>
                  <select
                    name="mortgage_type"
                    value={formData.mortgage_type}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-vista-silver-300 rounded-xl focus:ring-2 focus:ring-vista-teal-500 focus:border-vista-teal-500 transition-colors"
                  >
                    <option value="fixed">Fixed Rate</option>
                    <option value="variable">Variable Rate</option>
                    <option value="tracker">Tracker</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-vista-silver-700 mb-2">
                    Additional Fees (€)
                  </label>
                  <input
                    type="number"
                    name="fees_eur"
                    value={formData.fees_eur || ''}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-vista-silver-300 rounded-xl focus:ring-2 focus:ring-vista-teal-500 focus:border-vista-teal-500 transition-colors"
                    placeholder="10000"
                  />
                </div>
              </div>

              {/* Monthly Expenses */}
              <div>
                <label className="block text-sm font-medium text-vista-silver-700 mb-2">
                  Monthly Expenses (€)
                </label>
                <input
                  type="number"
                  name="monthly_expenses_eur"
                  value={formData.monthly_expenses_eur || ''}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-vista-silver-300 rounded-xl focus:ring-2 focus:ring-vista-teal-500 focus:border-vista-teal-500 transition-colors"
                  placeholder="800"
                />
              </div>

              {/* Calculate Button */}
              <button
                onClick={handleCalculate}
                disabled={loading}
                className="w-full bg-gradient-to-r from-vista-teal-500 to-vista-teal-600 text-white font-semibold py-4 px-6 rounded-xl hover:from-vista-teal-600 hover:to-vista-teal-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? 'Calculating...' : 'Calculate Mortgage'}
              </button>
            </div>
          </div>

          {/* Right Column - Results */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-vista-silver-200">
            <h2 className="text-xl font-bold text-vista-silver-900 mb-6">Mortgage Results</h2>
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                <p className="text-red-700">{error}</p>
              </div>
            )}

            {results && (
              <div className="space-y-6">
                {/* Summary Card */}
                <div className="bg-vista-teal-50 border border-vista-teal-200 rounded-xl p-6">
                  <h3 className="text-base font-semibold text-vista-teal-900 mb-4">Mortgage Summary</h3>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-vista-teal-100">
                      <span className="text-vista-silver-700 font-medium">Monthly Payment</span>
                      <span className="text-vista-teal-900 font-semibold text-base">
                        {formatCurrency(results.monthly_payment_eur)}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center py-2 border-b border-vista-teal-100">
                      <span className="text-vista-silver-700 font-medium">Loan Amount</span>
                      <span className="text-vista-teal-900 font-semibold">
                        {formatCurrency(results.loan_amount_eur)}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center py-2 border-b border-vista-teal-100">
                      <span className="text-vista-silver-700 font-medium">Loan to Value</span>
                      <span className="text-vista-teal-900 font-semibold">
                        {formatPercentage(results.loan_to_value_pct)}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center py-2 border-b border-vista-teal-100">
                      <span className="text-vista-silver-700 font-medium">Total Interest</span>
                      <span className="text-vista-teal-900 font-semibold">
                        {formatCurrency(results.total_interest_eur)}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center py-2">
                      <span className="text-vista-silver-700 font-medium">Total Payment</span>
                      <span className="text-vista-teal-900 font-semibold">
                        {formatCurrency(results.total_payment_eur)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Amortization Schedule */}
                {results.amortization_schedule && results.amortization_schedule.length > 0 && (
                  <div className="bg-vista-silver-50 border border-vista-silver-200 rounded-xl p-6">
                    <h3 className="text-base font-semibold text-vista-silver-900 mb-4">Amortization Schedule (First 12 Months)</h3>
                    
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-vista-silver-200">
                            <th className="text-left py-2 text-vista-silver-700 font-medium">Month</th>
                            <th className="text-right py-2 text-vista-silver-700 font-medium">Payment</th>
                            <th className="text-right py-2 text-vista-silver-700 font-medium">Principal</th>
                            <th className="text-right py-2 text-vista-silver-700 font-medium">Interest</th>
                            <th className="text-right py-2 text-vista-silver-700 font-medium">Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {results.amortization_schedule.slice(0, 12).map((payment: any, index: number) => (
                            <tr key={index} className="border-b border-vista-silver-100 last:border-b-0">
                              <td className="py-2 text-vista-silver-600">{payment.payment_number}</td>
                              <td className="py-2 text-right text-vista-silver-600">{formatCurrency(payment.payment_amount)}</td>
                              <td className="py-2 text-right text-vista-teal-600">{formatCurrency(payment.principal_payment)}</td>
                              <td className="py-2 text-right text-vista-silver-600">{formatCurrency(payment.interest_payment)}</td>
                              <td className="py-2 text-right text-vista-silver-600">{formatCurrency(payment.remaining_balance)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {results.message && (
                  <div className="bg-vista-silver-50 border border-vista-silver-200 rounded-xl p-4">
                    <p className="text-vista-silver-700">{results.message}</p>
                  </div>
                )}
              </div>
            )}

            {!results && !error && (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🏠</div>
                <p className="text-vista-silver-600">
                  Enter your mortgage parameters and click "Calculate Mortgage" to see the results.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MortgageCalculator;
